// LT-311 — Settings hub overview. Lands on `/settings`. Grid of cards
// pointing at each sub-route. Replaces the pre-LT-311 SettingsPage direct
// render (which defaulted to the Account tab).
import React from 'react';
import { Link } from 'react-router-dom';
import {
  User,
  Users,
  Layers,
  ShieldCheck,
  Mail,
  FileText,
  Variable,
  CalendarCheck,
  Calendar as CalendarIcon,
  Plug,
  PhoneIncoming,
  CreditCard,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useInboundEligibility, useSmsEligibility } from '../../utils/entitlements';

interface HubCard {
  slug: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  requires?: 'sms' | 'inbound';
}

const CARDS: HubCard[] = [
  { slug: 'account', label: 'Account', description: 'Profile, email, mobile number.', icon: User },
  { slug: 'team', label: 'Team', description: 'Members, roles, invites.', icon: Users },
  { slug: 'templates', label: 'Templates', description: 'Email and SMS scripts with merge fields.', icon: FileText },
  { slug: 'custom-fields', label: 'Custom Fields', description: 'Contact schema extensions.', icon: Variable },
  { slug: 'booking-pages', label: 'Booking Pages', description: 'Calendly-style pages for goal-met conversions.', icon: CalendarCheck },
  { slug: 'calendars', label: 'Calendars', description: 'Google, Microsoft availability sources.', icon: CalendarIcon },
  { slug: 'integrations', label: 'Integrations', description: 'Calendar + CRM connections.', icon: Plug },
  { slug: 'senders', label: 'Senders', description: 'Verified from-addresses for email steps.', icon: Mail },
  { slug: 'email-deliverability', label: 'Email Deliverability', description: 'SPF, DKIM, domain authentication.', icon: ShieldCheck },
  { slug: 'inbound-config', label: 'Inbound Config', description: 'Phone numbers, routing, greeting IVR.', icon: PhoneIncoming, requires: 'inbound' },
  { slug: 'pipelines', label: 'Pipelines', description: 'Sales pipeline stages and rules.', icon: Layers },
  { slug: 'billing', label: 'Billing', description: 'Subscription plan and credit balance.', icon: CreditCard },
];

const SettingsHubOverview: React.FC = () => {
  const { user } = useAuth();
  const { canSms } = useSmsEligibility(user);
  const { canInbound } = useInboundEligibility(user);

  const visibleCards = CARDS.filter((c) => {
    if (c.requires === 'sms' && !canSms) return false;
    if (c.requires === 'inbound' && !canInbound) return false;
    return true;
  });

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
      {visibleCards.map((card) => {
        const Icon = card.icon;
        return (
          <Link
            key={card.slug}
            to={`/settings/${card.slug}`}
            className="group bg-surface-1 border border-white/[0.08] rounded-ds-xl p-4 hover:border-accent-border hover:bg-surface-2 transition-colors"
          >
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-ds-lg bg-accent-tint border border-accent-border">
                <Icon className="w-4 h-4 text-accent" />
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold text-slate-50 group-hover:text-accent-text">
                  {card.label}
                </h3>
                <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
                  {card.description}
                </p>
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
};

export default SettingsHubOverview;
